import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Plus, LogIn, UserMinus, UserCheck, X as XIcon, Pencil, Trash2, UserPlus as UserPlusIcon, Share2, Shuffle, Clock, Ban, Bot, Trophy } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Bracket from "../components/Bracket";
import AuthModal from "../components/AuthModal";
import JoinTournamentModal from "../components/JoinTournamentModal";
import EditTournamentModal from "../components/EditTournamentModal";
import EditTeamModal from "../components/EditTeamModal";
import ShareModal from "../components/ShareModal";
import { CheckInPanel, CheckInBadge } from "../components/CheckInPanel";
import { api, formatError, resolveAsset } from "../lib/api";
import { useLiveWS } from "../hooks/useLiveWS";
import { useAuth } from "../lib/auth";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";
const TABS = ["all", "scheduled", "live", "finished"];

// Human format for the tournament detail card. Uses viewer's local timezone.
function formatTournamentStart(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  } catch {
    return "";
  }
}

export default function TournamentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeMatch, setActiveMatch] = useState(null);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);
  const [myRequestStatus, setMyRequestStatus] = useState(null); // pending | approved | rejected | null
  const [editingTournament, setEditingTournament] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [shareTournament, setShareTournament] = useState(null);

  const loadList = async () => {
    const { data } = await api.get("/tournaments");
    setTournaments(data);
    return data;
  };

  const loadOne = async (tid) => {
    const { data } = await api.get(`/tournaments/${tid}`);
    setSelected(data.tournament);
    setTeams(data.teams);
    setMatches(data.matches);
    if (user) {
      try {
        const { data: r } = await api.get(`/tournaments/${tid}/my-request`);
        setMyRequestStatus(r?.status || null);
      } catch {
        setMyRequestStatus(null);
      }
    } else {
      setMyRequestStatus(null);
    }
  };

  useEffect(() => {
    (async () => {
      const list = await loadList();
      const targetId = id || list.find((t) => t.status === "live")?.id || list[0]?.id;
      if (targetId) await loadOne(targetId);
    })();
  }, [id]);

  useLiveWS((msg) => {
    if (msg.event === "match_updated" && selected && msg.data.tournament_id === selected.id) {
      setMatches((prev) => prev.map((m) => (m.id === msg.data.id ? msg.data : m)));
    } else if (msg.event === "team_updated" && selected && msg.data?.tournament_id === selected.id) {
      setTeams((prev) => prev.map((t) => (t.id === msg.data.id ? msg.data : t)));
    } else if (
      msg.event === "bracket_generated" ||
      msg.event === "tournament_updated" ||
      msg.event === "tournament_created" ||
      msg.event === "tournament_deleted" ||
      msg.event === "teams_shuffled"
    ) {
      loadList();
      if (selected) loadOne(selected.id);
    }
  });

  const teamsById = useMemo(() => {
    const m = {};
    teams.forEach((t) => (m[t.id] = t));
    return m;
  }, [teams]);

  const filtered = tournaments.filter((t) => {
    if (filter === "all") return true;
    if (filter === "scheduled") return t.status === "scheduled" || t.status === "draft";
    if (filter === "finished") return t.status === "finished";
    return t.status === filter;
  });
  const canManageTournament = (t) => {
    if (!t || !user || !user.id) return false;
    if (user.role === "admin") return true;
    return Boolean(t.owner_id) && t.owner_id === user.id;
  };
  const isParticipant = selected && user && (selected.participant_ids || []).includes(user.id);
  const canEdit = canManageTournament(selected);

  const handleJoinLeave = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!selected) return;
    // Leave: existing participants can still leave.
    if (isParticipant) {
      setJoinBusy(true);
      try {
        await api.post(`/tournaments/${selected.id}/leave`);
        toast.success("You left the tournament");
        await loadOne(selected.id);
        await loadList();
      } catch (e) {
        toast.error(formatError(e.response?.data?.detail) || e.message);
      }
      setJoinBusy(false);
      return;
    }
    // Already pending approval → no-op.
    if (myRequestStatus === "pending") {
      toast.info("Your request is pending admin approval");
      return;
    }
    // Otherwise open the join form modal so we can collect IGN + player ID.
    setJoinModalOpen(true);
  };

  const onJoinSubmitted = async () => {
    if (!selected) return;
    await loadOne(selected.id);
    await loadList();
  };

  const handleCreate = () => {
    if (!user) { setAuthOpen(true); return; }
    if (user.role !== "admin") {
      toast.error("Only admins can create tournaments");
      return;
    }
    setCreateOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-right" theme="dark" toastOptions={{ className: "glass-dark !border-red-500/30" }} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10 flex flex-wrap justify-between items-end gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">
              Tournament hub
            </div>
            <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">
              Live Brackets
            </h1>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              All active, upcoming and past tournaments. Click any match in the bracket for live details.
            </p>
          </div>
          {user?.role === "admin" && (
            <button onClick={handleCreate} data-testid="create-tournament-btn" className="btn-liquid">
              <Plus size={14} /> Create Tournament
            </button>
          )}
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`tournaments-filter-${t}`}
              className={`px-5 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-all ${
                filter === t
                  ? "bg-red-600 text-white shadow-[0_0_18px_rgba(230,0,0,0.5)]"
                  : "glass-dark text-zinc-400 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-6">
          <aside className="space-y-3">
            {filtered.map((t) => (
              <div
                key={t.id}
                data-testid={`tournament-item-${t.id}`}
                className={`group relative w-full text-left glass-dark rounded-xl overflow-hidden transition-all ${
                  selected?.id === t.id ? "border-red-500/50 shadow-[0_0_20px_rgba(230,0,0,0.2)]" : "hover:border-red-500/30"
                }`}
              >
                {t.banner_url && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${resolveAsset(t.banner_url)})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      opacity: 0.18,
                    }}
                  />
                )}
                {t.banner_url && <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-black/85 via-black/55 to-black/40" />}
                <button
                  onClick={() => { navigate(`/tournaments/${t.id}`); loadOne(t.id); }}
                  className="relative w-full text-left p-4"
                >
                  <div className="flex items-center gap-2 mb-1">
                    {t.status === "live" && <span className="live-dot" />}
                    <span className={`text-[10px] uppercase tracking-widest font-mono ${
                      t.status === "live" ? "text-red-300" :
                      t.status === "scheduled" ? "text-amber-300" :
                      "text-zinc-500"
                    }`}>
                      {t.status}
                    </span>
                  </div>
                  <div className="font-semibold truncate pr-12">{t.name}</div>
                  <div className="mt-2 flex justify-between items-center text-xs">
                    <span className="text-zinc-400 uppercase tracking-widest font-mono truncate">
                      {t.game ? t.game : t.format.replace("_", " ")}
                    </span>
                    <span className="font-mono font-bold text-red-300">${(t.prize_pool || 0).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {(t.team_count ?? t.participant_ids?.length ?? 0)} / {t.max_teams || 32} slots
                  </div>
                </button>
                {user && (t.participant_ids || []).includes(user.id) && t.status !== "finished" && (
                  <div className="absolute top-3 left-3 z-10">
                    <span
                      data-testid={`list-joined-${t.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    >
                      <UserCheck size={9} /> Joined
                    </span>
                  </div>
                )}
                {canManageTournament(t) && (
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingTournament(t); }}
                      data-testid={`edit-tournament-${t.id}`}
                      className="h-7 w-7 rounded-full glass-red flex items-center justify-center hover:text-white"
                      title="Edit"
                    >
                      <Pencil size={11} />
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Delete "${t.name}"?`)) return;
                        try {
                          await api.delete(`/tournaments/${t.id}`);
                          toast.success("Tournament deleted");
                          await loadList();
                          if (selected?.id === t.id) setSelected(null);
                        } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
                      }}
                      data-testid={`delete-tournament-${t.id}`}
                      className="h-7 w-7 rounded-full glass-red flex items-center justify-center hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="glass-dark rounded-xl p-6 text-center text-zinc-500 text-sm">
                No tournaments match this filter.
              </div>
            )}
          </aside>

          <div>
            {selected ? (
              <>
                <div className="glass-dark rounded-2xl mb-4 overflow-hidden relative">
                  {selected.banner_url && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage: `url(${resolveAsset(selected.banner_url)})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                  )}
                  {selected.banner_url && <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-black/90 via-black/70 to-black/50" />}
                  <div className="relative p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">{selected.name}</h2>
                      <p className="text-zinc-300 mt-2">{selected.description}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Link
                        to={`/tournament/${selected.id}/bracket`}
                        data-testid="tournament-open-bracket"
                        className="btn-ghost-glass !px-5"
                      >
                        Open bracket →
                      </Link>
                      <button
                        onClick={() => setShareTournament(selected)}
                        data-testid="tournament-share-btn"
                        className="btn-ghost-glass !px-5"
                      >
                        <Share2 size={14} /> Share
                      </button>
                      {selected.status !== "finished" && (
                        <button
                          onClick={handleJoinLeave}
                          disabled={joinBusy || myRequestStatus === "pending" || myRequestStatus === "rejected"}
                          data-testid="tournament-join-btn"
                          className={
                            isParticipant
                              ? "btn-ghost-glass !px-5"
                              : myRequestStatus === "pending"
                                ? "btn-ghost-glass !px-5 !text-amber-300 !border-amber-400/40 cursor-not-allowed"
                                : myRequestStatus === "rejected"
                                  ? "btn-ghost-glass !px-5 !text-red-300 !border-red-500/40 cursor-not-allowed"
                                  : "btn-liquid !px-5"
                          }
                        >
                          {isParticipant ? <><UserMinus size={14} /> Leave</>
                            : myRequestStatus === "pending" ? <><Clock size={14} /> Pending approval</>
                            : myRequestStatus === "rejected" ? <><Ban size={14} /> Request rejected</>
                            : user ? <><UserCheck size={14} /> Request to join</>
                            : <><LogIn size={14} /> Sign in to join</>}
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => setEditingTournament(selected)}
                          data-testid="tournament-manage-btn"
                          className="btn-ghost-glass !px-5"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4 text-sm text-zinc-300">
                    <span>Format: <span className="text-white font-semibold">{selected.format.replace("_", " ")}</span></span>
                    <span>Prize: <span className="text-red-300 font-mono font-bold">${(selected.prize_pool || 0).toLocaleString()}</span></span>
                    <span>Slots: <span className="text-white font-semibold">{teams.length} / {selected.max_teams || 32}</span></span>
                    <span>Status: <span className={`font-semibold ${selected.status === "live" ? "text-red-300" : "text-white"}`}>{selected.status}</span></span>
                    {selected.starts_at && (
                      <span data-testid="tournament-starts-at">
                        Starts: <span className="text-white font-semibold">{formatTournamentStart(selected.starts_at)}</span>
                      </span>
                    )}
                    {selected.host && <span>Host: <span className="text-white font-semibold">{selected.host}</span></span>}
                  </div>
                  </div>
                </div>
                <CheckInPanel
                  tournament={selected}
                  teams={teams}
                  user={user}
                  canEdit={canEdit}
                  onChange={() => loadOne(selected.id)}
                />
                <div className="glass-dark rounded-2xl overflow-hidden hidden md:block">
                  {matches.length > 0 ? (
                    <Bracket
                      matches={matches}
                      teamsById={teamsById}
                      format={selected.format}
                      onMatchClick={(m) => setActiveMatch(m)}
                    />
                  ) : (
                    <div className="text-center text-zinc-500 py-24">Bracket not yet generated.</div>
                  )}
                </div>

                {/* Mobile-only compact prompt — the embedded bracket gets
                    unreadable on narrow phones, so we steer users to the
                    dedicated full-screen bracket page. */}
                <div className="md:hidden">
                  {matches.length > 0 ? (
                    <Link
                      to={`/tournament/${selected.id}/bracket`}
                      data-testid="tournament-open-bracket-mobile"
                      className="block glass-dark rounded-2xl p-5 border border-red-500/20 hover:border-red-500/40 transition-colors active:scale-[0.99]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                          <Trophy size={18} className="text-red-400" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-white">View full bracket</div>
                          <div className="text-[11px] text-zinc-500 font-mono">Tap for the zoomable mobile view →</div>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="glass-dark rounded-2xl text-center text-zinc-500 py-12 text-sm">
                      Bracket not yet generated.
                    </div>
                  )}
                </div>

                {/* Standings */}
                <div className="glass-dark rounded-2xl p-5 mt-4">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h3 className="font-display font-bold text-lg">Team Standings</h3>
                    {canEdit && (
                      <div className="flex gap-2 flex-wrap">
                        {!["live", "finished"].includes(selected.status) && teams.length >= 2 && (
                          <button
                            onClick={async () => {
                              if (!window.confirm("Shuffle team seeds? Any pre-generated bracket will be cleared.")) return;
                              try {
                                await api.post(`/tournaments/${selected.id}/shuffle`);
                                toast.success("Teams shuffled — seeds re-assigned");
                                await loadOne(selected.id);
                              } catch (e) {
                                toast.error(formatError(e.response?.data?.detail) || e.message);
                              }
                            }}
                            data-testid="shuffle-teams-btn"
                            className="btn-ghost-glass !py-1.5 !px-3 !text-xs"
                          >
                            <Shuffle size={12} /> Shuffle teams
                          </button>
                        )}
                        {!["live", "finished"].includes(selected.status) && teams.length < (selected.max_teams || 32) && (
                          <button
                            onClick={async () => {
                              const missing = (selected.max_teams || 32) - teams.length;
                              if (!window.confirm(`Fill ${missing} empty slot(s) with BOTs and distribute them evenly? Any pre-generated bracket will be cleared.`)) return;
                              try {
                                const res = await api.post(`/tournaments/${selected.id}/fill-bots`);
                                toast.success(`${res.data.added} bot(s) added — seeds re-shuffled`);
                                await loadOne(selected.id);
                              } catch (e) {
                                toast.error(formatError(e.response?.data?.detail) || e.message);
                              }
                            }}
                            data-testid="fill-bots-btn"
                            className="btn-ghost-glass !py-1.5 !px-3 !text-xs !text-amber-300 !border-amber-400/30"
                          >
                            <Bot size={12} /> Fill with bots
                          </button>
                        )}
                        <button
                          onClick={() => setAddTeamOpen(true)}
                          data-testid="add-team-btn"
                          className="btn-ghost-glass !py-1.5 !px-3 !text-xs"
                        >
                          <UserPlusIcon size={12} /> Add team
                        </button>
                      </div>
                    )}
                  </div>
                  <LayoutGroup>
                  <motion.div layout className="grid sm:grid-cols-2 gap-2">
                    {(() => {
                      const preLive = !["live", "finished"].includes(selected.status);
                      const ordered = [...teams].sort((a, b) =>
                        preLive
                          ? (a.seed || 0) - (b.seed || 0)
                          : (b.wins || 0) - (a.wins || 0)
                      );
                      return ordered.map((t, i) => (
                      <motion.div
                        key={t.id}
                        layout
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{
                          layout: { type: "spring", stiffness: 420, damping: 34 },
                          opacity: { duration: 0.2 },
                        }}
                        data-testid={`standing-${t.id}`}
                        className="group flex flex-wrap items-center justify-between gap-x-2 gap-y-1 py-2 px-3 rounded-lg hover:bg-white/5 will-change-transform"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-xs text-zinc-500 w-6 flex-shrink-0">{i + 1}.</span>
                          <span className="font-medium truncate">{t.name}</span>
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                          <CheckInBadge team={t} tournament={selected} canEdit={canEdit} onChange={() => loadOne(selected.id)} />
                          <span className="font-mono text-xs text-zinc-400 whitespace-nowrap">
                            <span className="text-emerald-400">{t.wins || 0}W</span>{" "}
                            <span className="text-red-400">{t.losses || 0}L</span>
                          </span>
                          {canEdit && (
                            <div className="flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => setEditingTeam(t)}
                                data-testid={`edit-team-${t.id}`}
                                className="h-6 w-6 rounded-full glass-red flex items-center justify-center hover:text-white"
                                title="Edit"
                              >
                                <Pencil size={10} />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Remove ${t.name}?`)) return;
                                  try {
                                    await api.delete(`/teams/${t.id}`);
                                    toast.success("Team removed");
                                    await loadOne(selected.id);
                                  } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
                                }}
                                data-testid={`delete-team-${t.id}`}
                                className="h-6 w-6 rounded-full glass-red flex items-center justify-center hover:text-red-400"
                                title="Remove"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                      ));
                    })()}
                  </motion.div>
                  </LayoutGroup>
                </div>
              </>
            ) : (
              <div className="glass-dark rounded-2xl p-16 text-center text-zinc-500">Select a tournament</div>
            )}
          </div>
        </div>
      </div>

      {activeMatch && (
        <div
          onClick={() => setActiveMatch(null)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="glass-red-glow rounded-2xl p-8 max-w-md w-full"
            data-testid="match-detail-modal"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-red-300 font-mono mb-2">
              {activeMatch.round_name || "Match"}
            </div>
            <h3 className="font-display font-bold text-2xl mb-6">Match Details</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between glass-dark rounded-xl p-4">
                <span className="font-semibold">{teamsById[activeMatch.team_a_id]?.name || "TBD"}</span>
                <span className="font-mono text-2xl font-bold text-red-300">{activeMatch.score_a}</span>
              </div>
              <div className="flex items-center justify-between glass-dark rounded-xl p-4">
                <span className="font-semibold">{teamsById[activeMatch.team_b_id]?.name || "TBD"}</span>
                <span className="font-mono text-2xl font-bold text-red-300">{activeMatch.score_b}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setActiveMatch(null)}
                data-testid="close-match-modal"
                className="btn-ghost-glass !py-2 !px-5"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer discordUrl={DISCORD_URL} />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode="login" />
      <JoinTournamentModal
        tournament={selected}
        open={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
        onSubmitted={onJoinSubmitted}
      />
      {createOpen && (
        <CreateTournamentModal
          onClose={() => setCreateOpen(false)}
          onCreated={async (t) => {
            setCreateOpen(false);
            toast.success("Tournament created");
            await loadList();
            await loadOne(t.id);
            navigate(`/tournaments/${t.id}`);
          }}
        />
      )}
      {editingTournament && (
        <EditTournamentModal
          tournament={editingTournament}
          teams={editingTournament.id === selected?.id ? teams : []}
          onClose={() => setEditingTournament(null)}
          onSaved={async () => { await loadList(); if (selected) await loadOne(selected.id); }}
          onDeleted={async (id) => { await loadList(); if (selected?.id === id) setSelected(null); }}
        />
      )}
      {editingTeam && (
        <EditTeamModal
          team={editingTeam}
          onClose={() => setEditingTeam(null)}
          onSaved={async () => { if (selected) await loadOne(selected.id); }}
          onDeleted={async () => { if (selected) await loadOne(selected.id); }}
        />
      )}
      {addTeamOpen && selected && (
        <AddTeamModal
          tournament={selected}
          onClose={() => setAddTeamOpen(false)}
          onCreated={async () => { setAddTeamOpen(false); await loadOne(selected.id); }}
        />
      )}
      {shareTournament && (
        <ShareModal
          tournament={shareTournament}
          onClose={() => setShareTournament(null)}
        />
      )}
    </div>
  );
}

function AddTeamModal({ tournament, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", color: "#E60000", seed: 0 });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${tournament.id}/teams`, { ...form, seed: Number(form.seed) || 0 });
      toast.success("Team added");
      onCreated();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="add-team-modal"
        className="glass-red-glow rounded-2xl max-w-md w-full p-8"
      >
        <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-1">New competitor</div>
        <h2 className="font-display font-black text-2xl mb-6">Add team to {tournament.name}</h2>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Team name</span>
            <input
              data-testid="add-team-name" required autoFocus value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Color</span>
              <input
                data-testid="add-team-color" type="color" value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-2 h-11"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Seed</span>
              <input
                data-testid="add-team-seed" type="number" value={form.seed}
                onChange={(e) => setForm({ ...form, seed: e.target.value })}
                className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
              />
            </label>
          </div>
          <div className="flex justify-between pt-2">
            <button type="button" onClick={onClose} className="btn-ghost-glass !px-4">Cancel</button>
            <button type="submit" disabled={busy} data-testid="add-team-submit" className="btn-liquid !px-5">
              <Plus size={14} /> {busy ? "Adding..." : "Add team"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateTournamentModal({ onClose, onCreated }) {
  // Default start = tomorrow at 18:00 local
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(18, 0, 0, 0);
  const defaultDate = tomorrow.toISOString().slice(0, 10);
  const defaultTime = "18:00";

  const [form, setForm] = useState({
    name: "", description: "", format: "single_elimination",
    prize_pool: 0, game: "", host: "", max_teams: 32,
    starts_date: defaultDate, starts_time: defaultTime,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    if (!form.starts_date || !form.starts_time) {
      toast.error("Start date and time are required");
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(`${form.starts_date}T${form.starts_time}`).toISOString();
      const { data } = await api.post("/tournaments", {
        name: form.name,
        description: form.description,
        format: form.format,
        game: form.game,
        host: form.host,
        prize_pool: Number(form.prize_pool) || 0,
        max_teams: Math.max(2, Math.min(32, Number(form.max_teams) || 32)),
        starts_at: iso,
      });
      onCreated(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="create-tournament-modal"
        className="glass-red-glow rounded-2xl max-w-lg w-full p-8 relative"
      >
        <button
          onClick={onClose}
          data-testid="create-modal-close"
          className="absolute top-4 right-4 h-8 w-8 rounded-full glass-dark flex items-center justify-center hover:text-red-400"
        >
          <XIcon size={14} />
        </button>
        <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-1">New event</div>
        <h2 className="font-display font-black text-3xl tracking-tight mb-6">Create Tournament</h2>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name" testid="create-t-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Game" testid="create-t-game" value={form.game} onChange={(v) => setForm({ ...form, game: v })} placeholder="Valorant" />
            <Field label="Host" testid="create-t-host" value={form.host} onChange={(v) => setForm({ ...form, host: v })} placeholder="Your name or org" />
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Format</span>
            <select
              data-testid="create-t-format"
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value })}
              className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
            >
              <option value="single_elimination">Single Elimination</option>
              <option value="double_elimination">Double Elimination</option>
              <option value="round_robin">Round Robin</option>
              <option value="group_advancement">Group Advancement (Top 2)</option>
            </select>
          </label>
          <Field label="Prize Pool ($)" testid="create-t-prize" type="number" value={form.prize_pool} onChange={(v) => setForm({ ...form, prize_pool: v })} />
          <Field label="Max teams (2-32)" testid="create-t-max" type="number" value={form.max_teams} onChange={(v) => setForm({ ...form, max_teams: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date" testid="create-t-date" type="date" value={form.starts_date} onChange={(v) => setForm({ ...form, starts_date: v })} required />
            <Field label="Start time" testid="create-t-time" type="time" value={form.starts_time} onChange={(v) => setForm({ ...form, starts_time: v })} required />
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Description</span>
            <textarea
              data-testid="create-t-desc"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500 resize-none"
            />
          </label>
          <button
            type="submit" disabled={busy}
            data-testid="create-t-submit"
            className="btn-liquid btn-liquid-lg w-full"
          >
            <Plus size={14} /> {busy ? "Creating..." : "Create tournament"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, required }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <input
        data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
      />
    </label>
  );
}
